import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Award,
  MapPin,
  Star,
  ArrowRight,
  Shield
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const IndianHarbourBeachPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Consistent, detail-oriented cleaning for Indian Harbour Beach homes. Weekly or bi-weekly service with the same professional team."
    },
    {
      title: "Deep Cleaning",
      description: "Meticulous cleaning for beachfront properties. Salt air, sand, and humidity management with attention to every detail."
    },
    {
      title: "Move-In Cleaning",
      description: "Start fresh in your new Indian Harbour Beach home with a spotless, sanitized space before you unpack."
    },
    {
      title: "Vacation Rental Cleaning",
      description: "Premium turnover service for beachside vacation rentals with guest-ready standards."
    }
  ];

  const faqs = [
    {
      question: "Do you serve all of Indian Harbour Beach?",
      answer: "Yes. We serve all Indian Harbour Beach neighborhoods—from oceanfront properties along A1A to the residential areas near the Indian Harbour Beach Aquatic Center and throughout the community. We're familiar with the area and the unique needs of beachfront homes."
    },
    {
      question: "How do you handle high-end finishes and fixtures?",
      answer: "Heather's 15+ years of experience includes working with luxury materials and modern finishes. We know which products to use on natural stone, high-end appliances, and custom surfaces. Your home is in good hands."
    },
    {
      question: "What makes Pierce Premier different from other cleaning services?",
      answer: "We're locally owned by Bob and Heather—not a franchise. You'll have the same professional cleaners every visit, and we personally oversee every job to ensure it meets our standards. That consistency and accountability matters."
    },
    {
      question: "Can you work around our schedule?",
      answer: "Absolutely. We're flexible and work with your availability. Many of our Indian Harbour Beach clients have specific scheduling needs, and we accommodate them."
    },
    {
      question: "Are you fully insured?",
      answer: "Yes. We're fully licensed and insured. Bob and Heather take responsibility for every job we do."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Indian Harbour Beach, FL | Pierce Premier",
      description: "Premium house cleaning in Indian Harbour Beach, FL. Beachfront specialists with attention to detail. Same professional team, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/indian-harbour-beach"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Indian Harbour Beach" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Indian Harbour Beach",
      description: "Premium house cleaning services in Indian Harbour Beach, FL including recurring cleaning, deep cleaning, move-in preparation, and vacation rental turnover."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Indian Harbour Beach, FL | Pierce Premier"
        description="Premium house cleaning in Indian Harbour Beach, FL. Beachfront specialists. Same professional team every visit, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/indian-harbour-beach"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  <Badge className="bg-emerald-600 text-white">Beachfront Excellence</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Indian Harbour Beach, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Indian Harbour Beach is known for beautiful oceanfront properties, well-maintained neighborhoods, and residents who appreciate quality. Pierce Premier serves this community with cleaning services that meet those same high standards.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you own a beachfront home, live near the Indian Harbour Beach Aquatic Center, or manage a vacation rental property, we bring the professionalism and attention to detail you expect.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-emerald-600 text-emerald-600 px-8 py-4 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80" 
                  alt="Premium house cleaning in Indian Harbour Beach, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Indian Harbour Beach Homeowners Choose Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Professional cleaning that respects your home and your standards.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Attention to Detail</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Indian Harbour Beach homeowners expect quality, and we deliver it. Heather's 15+ years of experience means nothing gets overlooked—from baseboards to ceiling fans to every corner of your home.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Respect for Your Investment</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your home represents a significant investment. We treat it with care and professionalism, using quality products and methods that protect your finishes, fixtures, and furnishings.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Fully Insured & Trustworthy</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We're fully licensed and insured, and Bob and Heather personally oversee every job. You're inviting us into your home—we take that responsibility seriously.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Consistent Professional Team</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Same trusted cleaners every visit means consistent results. No rotating crews, no surprises. Just reliable, professional cleaning you can count on.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Indian Harbour Beach
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Indian Harbour Beach has a reputation for well-maintained properties and residents who take pride in their community. It's the kind of place where people invest in quality and expect the same from their service providers.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We appreciate the opportunity to serve homeowners who value professionalism and consistency. When you trust Pierce Premier with your home, we make sure that trust is earned every time.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for Indian Harbour Beach Homes
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-emerald-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Indian Harbour Beach Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Oceanfront A1A",
                "Aquatic Center Area",
                "Cove Road",
                "Eau Gallie Blvd Area",
                "Riverview Drive",
                "Community Neighborhoods"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Code:</span> 32937
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Beachside Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Satellite Beach", link: "/areas/satellite-beach" },
                { name: "Indialantic", link: "/areas/indialantic" },
                { name: "Melbourne Beach", link: "#" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Palm Bay", link: "/areas/palm-bay" }
              ].map((city, idx) => (
                city.link === "#" ? (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <span className="text-gray-600">{city.name}</span>
                  </div>
                ) : (
                  <Link key={idx} to={city.link} className="p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200 hover:border-emerald-400 transition-colors text-center group">
                    <span className="text-emerald-700 font-semibold group-hover:text-emerald-900">{city.name}</span>
                    <ArrowRight className="w-4 h-4 inline-block ml-1 text-emerald-600" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Indian Harbour Beach Residents
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-emerald-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;Best deep cleaning service in Brevard County! They got stains out I thought were permanent. Amazing attention to detail.&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">David Chen</p>
                <p className="text-gray-500 text-sm">Viera, FL</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <CTASection 
          title="Ready for Professional Beachside Cleaning?"
          description="Let Pierce Premier take care of your Indian Harbour Beach home. Same professional team, attention to detail, and the quality you expect."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default IndianHarbourBeachPage;